import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../api/config';
import toast from 'react-hot-toast';
import {
  Store, Mail, Phone, MapPin, Clock, Ruler,
  ShoppingBag, LogOut, Save, BadgeCheck, Building2, ChevronRight,
} from 'lucide-react';

export default function Profile() {
  const { vendor, logout } = useAuth();
  const vd = vendor?.vendor || vendor;

  const [isOpen,  setIsOpen]  = useState(vd?.isOpen ?? true);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({
    shopName:       vd?.shopName || vd?.businessName || vd?.name || '',
    phone:          vd?.phone    || '',
    address:        vd?.address  || '',
    deliveryRadius: vd?.deliveryRadius || 5,
    minOrder:       vd?.minOrder       || 0,
    deliveryTime:   vd?.deliveryTime   || 30,
  });

  const toggleShop = async () => {
    try {
      await API.patch(`/vendors/${vd?._id}/profile`, { isOpen: !isOpen });
      setIsOpen(o => !o);
      toast.success(!isOpen ? '🟢 Shop khul gayi!' : '🔴 Shop band ho gayi!');
    } catch { toast.error('Status update nahi hua!'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.patch(`/vendors/${vd?._id}/profile`, form);
      toast.success('Profile save ho gayi!');
    } catch { toast.error('Save nahi hua!'); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));

  return (
    <div style={S.page}>
      {/* Shop status */}
      <div style={S.statusCard}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:44, height:44, borderRadius:14, background: isOpen ? '#f0fdf4' : '#fef2f2', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Store size={22} color={isOpen ? '#10b981' : '#ef4444'}/>
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'#1a1a1a' }}>Shop Status</div>
            <div style={{ fontSize:13, color: isOpen ? '#10b981' : '#ef4444', marginTop:2, fontWeight:500 }}>
              {isOpen ? '🟢 Abhi khuli hai' : '🔴 Abhi band hai'}
            </div>
          </div>
        </div>
        <div style={{ ...S.toggle, background: isOpen ? '#10b981' : '#d1d5db' }} onClick={toggleShop}>
          <div style={{ ...S.thumb, transform: isOpen ? 'translateX(24px)' : 'translateX(2px)' }}/>
        </div>
      </div>

      {/* Account info */}
      <div style={S.card}>
        <div style={S.cardHead}><BadgeCheck size={16} color='#ff6b35'/> Account Info</div>
        {[
          { icon:<Mail size={14} color='#bbb'/>,      label:'Email',         value:vd?.email },
          { icon:<Building2 size={14} color='#bbb'/>, label:'Business Type', value:vd?.businessType },
          { icon:<BadgeCheck size={14} color='#bbb'/>,label:'Status',        value:vd?.status, isStatus:true },
        ].map((r,i) => (
          <div key={i} style={S.infoRow}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>{r.icon}<span style={{ fontSize:13, color:'#888' }}>{r.label}</span></div>
            {r.isStatus ? (
              <span style={{ padding:'3px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                background: vd?.status==='approved' ? '#f0fdf4' : '#fef2f2',
                color:      vd?.status==='approved' ? '#16a34a' : '#ef4444' }}>
                {vd?.status}
              </span>
            ) : (
              <span style={{ fontSize:13, color:'#1a1a1a', fontWeight:500, textTransform:'capitalize' }}>{r.value||'N/A'}</span>
            )}
          </div>
        ))}
      </div>

      {/* Shop settings */}
      <div style={S.card}>
        <div style={S.cardHead}><Store size={16} color='#ff6b35'/> Shop Settings</div>

        {[
          { label:'Shop Name', key:'shopName', placeholder:'Shop ka naam', icon:<Store size={14} color='#bbb'/> },
          { label:'Phone',     key:'phone',    placeholder:'Phone number',  icon:<Phone size={14} color='#bbb'/> },
          { label:'Address',   key:'address',  placeholder:'Shop ka address',icon:<MapPin size={14} color='#bbb'/> },
        ].map(f => (
          <div key={f.key} style={S.fGroup}>
            <label style={S.label}>{f.icon} {f.label}</label>
            <input style={S.input} value={form[f.key]} placeholder={f.placeholder}
              onChange={e => set(f.key, e.target.value)}/>
          </div>
        ))}

        <div style={{ display:'flex', gap:12 }}>
          {[
            { label:'Min Order (₹)', key:'minOrder',     icon:<ShoppingBag size={13} color='#bbb'/>, type:'number' },
            { label:'Delivery (min)',key:'deliveryTime',  icon:<Clock size={13} color='#bbb'/>,       type:'number' },
          ].map(f => (
            <div key={f.key} style={{ ...S.fGroup, flex:1 }}>
              <label style={S.label}>{f.icon} {f.label}</label>
              <input style={S.input} type={f.type} value={form[f.key]} onChange={e => set(f.key, e.target.value)}/>
            </div>
          ))}
        </div>

        <div style={S.fGroup}>
          <label style={S.label}><Ruler size={13} color='#bbb'/> Delivery Radius — {form.deliveryRadius} km</label>
          <input style={{ width:'100%', accentColor:'#ff6b35', marginBottom:4 }}
            type='range' min='1' max='20' value={form.deliveryRadius}
            onChange={e => set('deliveryRadius', e.target.value)}/>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#bbb' }}>
            <span>1 km</span><span>20 km</span>
          </div>
        </div>

        <button style={{ ...S.saveBtn, opacity:saving ? 0.7 : 1 }} onClick={save} disabled={saving}>
          <Save size={17} color='#fff'/>
          {saving ? 'Save ho raha hai...' : 'Profile Save Karo'}
        </button>
      </div>

      {/* Logout */}
      <button style={S.logout} onClick={logout}>
        <LogOut size={17} color='#ef4444'/> Logout
      </button>
    </div>
  );
}

const S = {
  page:       { padding:'16px', paddingBottom:80, background:'#f8f8f8', minHeight:'100vh' },
  statusCard: { background:'#fff', borderRadius:16, padding:'16px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', boxShadow:'0 2px 12px rgba(0,0,0,0.07)', marginBottom:14 },
  toggle:     { width:50, height:26, borderRadius:13, cursor:'pointer', position:'relative', transition:'background .3s', flexShrink:0 },
  thumb:      { position:'absolute', top:3, width:20, height:20, background:'#fff', borderRadius:'50%', transition:'transform .3s', boxShadow:'0 2px 4px rgba(0,0,0,0.2)' },
  card:       { background:'#fff', borderRadius:16, padding:'16px 18px', marginBottom:14, boxShadow:'0 2px 12px rgba(0,0,0,0.07)' },
  cardHead:   { display:'flex', alignItems:'center', gap:7, fontWeight:700, fontSize:15, color:'#1a1a1a', marginBottom:14, paddingBottom:12, borderBottom:'1px solid #f5f5f5' },
  infoRow:    { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 },
  fGroup:     { marginBottom:14 },
  label:      { display:'flex', alignItems:'center', gap:6, fontSize:13, color:'#666', marginBottom:7, fontWeight:500 },
  input:      { width:'100%', padding:'11px 14px', border:'1.5px solid #f0f0f0', borderRadius:12, fontSize:14, color:'#1a1a1a', background:'#fafafa' },
  saveBtn:    { width:'100%', padding:13, background:'linear-gradient(135deg,#ff6b35,#f7931e)', color:'#fff', borderRadius:12, fontSize:15, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginTop:6, boxShadow:'0 4px 16px rgba(255,107,53,0.3)' },
  logout:     { width:'100%', padding:13, background:'#fef2f2', color:'#ef4444', borderRadius:12, fontSize:15, fontWeight:700, marginBottom:30, display:'flex', alignItems:'center', justifyContent:'center', gap:8 },
};